const EPISODE_RE = /^@episode\s+([^\s]+)\s+"([^"]+)"\s*\{$/;
const BG_RE = /^([@&])bg\s+set\s+([^\s]+)(?:\s+([^\s{]+))?/;
// CG is now a single-line leaf: `@cg <name> "<content>"` (no block).
const CG_RE = /^([@&])cg\s+([^\s]+)\s+"([^"]*)"\s*$/;
// Character pose: `@<char> <pose> [transition]`. Replaces show/look/move/hide —
// the engine derives position from gamestate.MC and hides the prior char
// implicitly (one-on-screen rule). Guarded by !isKnownDirective(char).
const CHAR_POSE_RE = /^([@&])([A-Za-z][\w-]*)\s+([^\s]+)(?:\s+([^\s]+))?\s*$/;
const CHAR_BUBBLE_RE = /^([@&])([A-Za-z][\w-]*)\s+bubble\s+([^\s]+)/;
const DIALOGUE_RE = /^([A-Z][A-Z0-9_-]*)(?:\s+\[([^\]]+)\])?:\s?(.*)$/;
// Phone is a single block delimiting the overlay lifetime; only @text inside.
const PHONE_RE = /^([@&])phone\s*\{$/;
const TEXT_MESSAGE_RE = /^\s*@text\s+(from|to)\s+([A-Za-z][\w-]*):\s?(.*)$/;
// Audio collapsed: `@music <name>` (engine picks silence/crossfade) and
// `@music stop`; `@sfx <name>`. The play/crossfade/fadeout forms are gone.
const MUSIC_RE = /^([@&])music\s+([^\s]+)\s*$/;
const SFX_RE = /^([@&])sfx\s+([^\s]+)\s*$/;
// New leaf interaction beat.
const TRICK_RE = /^([@&])trick\s+([^\s]+)\s+"([^"]*)"\s*$/;
const MINIGAME_RE = /^([@&])minigame\s+([^\s]+)\s+"([^"]*)"\s*$/;
const CHOICE_RE = /^([@&])choice\s*\{$/;
const OPTION_RE = /^\s*@option\s+([^\s]+)\s+(safe|brave)\s+"([^"]*)"\s*\{$/;
const AFFECTION_RE = /^([@&])affection\s+([^\s]+)\s+([+-]\d+)/;
const SIGNAL_MARK_RE = /^([@&])signal\s+mark\s+(.+)$/;
const SIGNAL_INT_RE = /^([@&])signal\s+int\s+([^\s]+)\s*([=+-])\s*(-?\d+)/;
const BUTTERFLY_RE = /^([@&])butterfly\s+"([^"]*)"/;
const ACHIEVEMENT_RE = /^([@&])achievement\s+([^\s]+)\s*\{$/;
const IF_RE = /^([@&])if\s+\((.*)\)\s*\{$/;
// `@pause` is argument-free (repeat for longer beats).
const PAUSE_RE = /^([@&])pause\s*$/;
const GATE_RE = /^([@&])gate\s*\{$/;
// Lenient fallback patterns. These ONLY run after every strict pattern
// above has failed (see lenientStatementToCard). They allow required
// fields to be empty so that newly-inserted-but-not-yet-filled cards
// (e.g. character.show with empty character) survive emit/re-parse as
// the intended kind instead of falling through to kind="raw". Cards
// matched by these patterns carry fields.incomplete = true and
// fields.missingFields = [...] so consumers can decide their own
// severity (Studio webview: non-blocking yellow badge; LSP / ls
// validate: diagnostic / error).
// `@<char>` with no pose yet — the keyword-free pose syntax means an
// incomplete character card is just the bare character token.
const CHAR_POSE_LENIENT = /^([@&])([A-Za-z][\w-]*)\s*$/;
const CHAR_BUBBLE_LENIENT = /^([@&])\s*([A-Za-z][\w-]*)?\s*bubble(?:\s+(\S+))?\s*$/;
const SFX_LENIENT = /^([@&])sfx\s*$/;
const MUSIC_LENIENT = /^([@&])music\s*$/;
const BG_LENIENT = /^([@&])bg\s+set\s*(\S+)?\s*(\S+)?\s*$/;
const AFFECTION_LENIENT = /^([@&])affection\s+([A-Za-z][\w-]*)?\s*([+-]\d+)?\s*$/;
const KNOWN_DIRECTIVES = new Set([
    "bg", "cg", "phone", "text", "music", "sfx", "minigame", "trick", "choice", "affection", "signal",
    "butterfly", "if", "else", "episode", "option", "gate", "next", "end", "pause",
    "check", "achievement"
]);
// Retired verbs from the old show/look/move/hide quartet. The new pose syntax
// is `@<char> <pose>` where <pose> is a real look name, never one of these
// verbs. Guard the strict pose match so legacy two-token `@char look <sprite>`
// falls through to raw instead of silently mis-parsing the literal verb as the
// pose (look="look", transition="<sprite>") — matching how `@char show X at Y`
// already behaves.
const RESERVED_POSE = new Set(["show", "look", "move", "hide", "bubble"]);
export function emptyAssetMapping() {
    return {
        anchors: [],
        backgrounds: [],
        cg: [],
        characters: [],
        minigames: [],
        music: [],
        portraits: [],
        sfx: [],
        sprites: []
    };
}
export function normalizeAssetMapping(input) {
    const source = (input && typeof input === "object" ? input : {});
    const assets = (source.assets && typeof source.assets === "object" ? source.assets : source);
    const baseUrl = typeof source.base_url === "string" ? source.base_url : typeof source.baseUrl === "string" ? source.baseUrl : "";
    const minigames = normalizeEntries(assets.minigames, baseUrl);
    return {
        characters: normalizeCharacters(assets.characters ?? assets.chars, baseUrl),
        backgrounds: normalizeEntries(assets.backgrounds ?? assets.bg, baseUrl),
        music: normalizeEntries(assets.music, baseUrl),
        sfx: normalizeEntries(assets.sfx, baseUrl),
        cg: normalizeEntries(assets.cg, baseUrl),
        minigames: minigames.length > 0 ? minigames : normalizeEntries(source.minigames),
        // Workshop-extension arrays (sync-to-cloud writes these; legacy producer
        // key names accepted as aliases so older artifacts still resolve).
        portraits: normalizeIdEntries(assets.portraits ?? assets.character_portraits, baseUrl),
        anchors: normalizeAnchors(assets.anchors ?? assets.outfit_anchors, baseUrl),
        sprites: normalizeIdEntries(assets.sprites ?? assets.ep_sprites, baseUrl)
    };
}
export function parseLsToStudioModel(source) {
    const lines = source.replace(/\r\n/g, "\n").split("\n");
    const firstEpisodeLine = lines.find((line) => line.trim().startsWith("@episode"));
    const episodeMatch = firstEpisodeLine?.trim().match(EPISODE_RE);
    const model = {
        cards: [],
        episodeKey: episodeMatch?.[1] ?? "main:01",
        title: episodeMatch?.[2] ?? "Untitled"
    };
    const bodyLines = episodeBodyLines(lines);
    let index = 0;
    for (let i = 0; i < bodyLines.length;) {
        const line = bodyLines[i]?.trim() ?? "";
        if (line.length === 0 || line.startsWith("//") || line.startsWith("#")) {
            i++;
            continue;
        }
        const statement = readStatement(bodyLines, i);
        let sourceText = statement.source;
        i = statement.nextIndex;
        const card = statementToCard(sourceText, index++);
        if (sourceText.trim().length > 0) {
            model.cards.push(card);
        }
    }
    return model;
}
export function emitStudioModel(model) {
    const lines = [`@episode ${model.episodeKey} "${escapeQuote(model.title)}" {`];
    for (const card of model.cards) {
        pushIndented(lines, emitCard(card), "  ");
    }
    lines.push("}");
    return `${lines.join("\n")}\n`;
}
export function createCard(kind, title, source, index) {
    return {
        fields: {},
        id: `${kind}-${index}`,
        kind,
        source,
        title
    };
}
function statementToCard(source, index) {
    const line = firstLine(source);
    const bg = line.match(BG_RE);
    if (bg?.[2]) {
        const card = createCard("bg", `bg ${bg[2]}`, source, index);
        card.fields = { bg: bg[2], concurrent: bg[1] === "&", transition: bg[3] ?? "" };
        return card;
    }
    const cg = line.match(CG_RE);
    if (cg?.[2]) {
        const card = createCard("cg", `CG: ${cg[2]}`, source, index);
        card.fields = {
            cg: cg[2],
            concurrent: cg[1] === "&",
            content: cg[3] ?? ""
        };
        return card;
    }
    // Bubble must precede the generic pose match: `@josie bubble heart` would
    // otherwise read as pose="bubble" (a reserved word).
    const bubble = line.match(CHAR_BUBBLE_RE);
    if (bubble?.[2] && bubble[3] && !isKnownDirective(bubble[2])) {
        const card = createCard("character.bubble", `Bubble ${bubble[2]}`, source, index);
        card.fields = { bubble: bubble[3], character: bubble[2], concurrent: bubble[1] === "&" };
        return card;
    }
    // `@<char> <pose> [transition]` — show character / change pose. Replaces the
    // old show/look/move/hide quartet (position derived by the engine).
    const pose = line.match(CHAR_POSE_RE);
    if (pose?.[2] && pose[3] && !RESERVED_POSE.has(pose[3]) && !isKnownDirective(pose[2])) {
        const card = createCard("character.show", `Show ${pose[2]}`, source, index);
        card.fields = { character: pose[2], concurrent: pose[1] === "&", look: pose[3], transition: pose[4] ?? "" };
        return card;
    }
    const dialogue = line.match(DIALOGUE_RE);
    if (dialogue?.[1]) {
        const kind = dialogue[1] === "NARRATOR" ? "narration" : dialogue[1] === "YOU" ? "monologue" : "dialogue";
        const card = createCard(kind, dialogue[1], source, index);
        card.fields = { character: dialogue[1], look: dialogue[2] ?? "", text: dialogue[3] ?? "" };
        return card;
    }
    const phone = line.match(PHONE_RE);
    if (phone) {
        const card = createCard("phone", "Phone", source, index);
        card.fields = {
            concurrent: phone[1] === "&",
            messages: parseTextMessages(source)
        };
        return card;
    }
    const music = line.match(MUSIC_RE);
    if (music?.[2]) {
        const stop = music[2] === "stop";
        const card = createCard("music", stop ? "Music: stop" : `Music: ${music[2]}`, source, index);
        card.fields = stop
            ? { action: "stop", concurrent: music[1] === "&", track: "" }
            : { action: "play", concurrent: music[1] === "&", track: music[2] };
        return card;
    }
    const sfx = line.match(SFX_RE);
    if (sfx?.[2]) {
        const card = createCard("sfx", `SFX: ${sfx[2]}`, source, index);
        card.fields = { concurrent: sfx[1] === "&", sound: sfx[2] };
        return card;
    }
    const trick = line.match(TRICK_RE);
    if (trick?.[2]) {
        const card = createCard("trick", `Trick: ${trick[2]}`, source, index);
        card.fields = { concurrent: trick[1] === "&", description: trick[3] ?? "", trick: trick[2] };
        return card;
    }
    const minigame = line.match(MINIGAME_RE);
    if (minigame?.[2]) {
        const card = createCard("minigame", `Minigame: ${minigame[2]}`, source, index);
        card.fields = {
            concurrent: minigame[1] === "&",
            description: minigame[3] ?? "",
            minigame: minigame[2]
        };
        return card;
    }
    const choice = line.match(CHOICE_RE);
    if (choice) {
        const card = createCard("choice", "Choice", source, index);
        card.fields = { body: blockBody(source), concurrent: choice[1] === "&", options: parseOptions(source) };
        return card;
    }
    const affection = line.match(AFFECTION_RE);
    if (affection?.[2]) {
        const card = createCard("record", `Affection: ${affection[2]}`, source, index);
        card.fields = { character: affection[2], concurrent: affection[1] === "&", delta: affection[3] ?? "", recordType: "affection" };
        return card;
    }
    const signalInt = line.match(SIGNAL_INT_RE);
    if (signalInt?.[2]) {
        const card = createCard("record", `Signal Int: ${signalInt[2]}`, source, index);
        card.fields = { concurrent: signalInt[1] === "&", name: signalInt[2], op: signalInt[3] ?? "=", recordType: "signal.int", value: Number(signalInt[4] ?? 0) };
        return card;
    }
    const signalMark = line.match(SIGNAL_MARK_RE);
    if (signalMark?.[2]) {
        const card = createCard("record", `Signal: ${signalMark[2]}`, source, index);
        card.fields = { concurrent: signalMark[1] === "&", event: unquote(signalMark[2].trim()), recordType: "signal.mark" };
        return card;
    }
    const butterfly = line.match(BUTTERFLY_RE);
    if (butterfly?.[2]) {
        const card = createCard("record", "Butterfly", source, index);
        card.fields = { concurrent: butterfly[1] === "&", description: butterfly[2], recordType: "butterfly" };
        return card;
    }
    const achievement = line.match(ACHIEVEMENT_RE);
    if (achievement?.[2]) {
        const card = createCard("record", `Achievement: ${achievement[2]}`, source, index);
        card.fields = {
            achievement: achievement[2],
            concurrent: achievement[1] === "&",
            description: fieldLine(source, "description"),
            name: fieldLine(source, "name"),
            rarity: fieldLine(source, "rarity"),
            recordType: "achievement"
        };
        return card;
    }
    const condition = line.match(IF_RE);
    if (condition?.[2]) {
        const card = createCard("condition", "Condition", source, index);
        card.fields = { body: blockBody(source), concurrent: condition[1] === "&", condition: condition[2] };
        return card;
    }
    const pause = line.match(PAUSE_RE);
    if (pause) {
        const card = createCard("pause", "Pause", source, index);
        card.fields = { concurrent: pause[1] === "&" };
        return card;
    }
    const gate = line.match(GATE_RE);
    if (gate) {
        const card = createCard("gate", "Gate", source, index);
        card.fields = { body: blockBody(source), concurrent: gate[1] === "&" };
        return card;
    }
    const lenient = lenientStatementToCard(source, line, index);
    if (lenient) {
        return lenient;
    }
    return createCard("raw", "Raw", source, index);
}
/**
 * Lenient fallback identification. Runs only after every strict pattern
 * in statementToCard has failed. Returns a normal kind (e.g.
 * character.show) with fields.incomplete = true and a fields.missingFields
 * list so the Studio UI keeps rendering the proper block (non-blocking
 * yellow warning) while LSP / lsc validate can treat it as a diagnostic.
 *
 * Returns undefined if even the lenient patterns don't recognize the
 * line — caller falls through to kind="raw".
 */
function lenientStatementToCard(source, line, index) {
    // `@<char>` with no pose yet — keep it a character.show so the Studio block
    // keeps rendering (incomplete = missing "look").
    const poseMatch = line.match(CHAR_POSE_LENIENT);
    if (poseMatch && poseMatch[2] && !isKnownDirective(poseMatch[2])) {
        const card = createCard("character.show", `Show ${poseMatch[2]}`, source, index);
        card.fields = {
            character: poseMatch[2],
            concurrent: poseMatch[1] === "&",
            incomplete: true,
            look: "",
            missingFields: ["look"]
        };
        return card;
    }
    const bubbleMatch = line.match(CHAR_BUBBLE_LENIENT);
    if (bubbleMatch && !isKnownDirective(bubbleMatch[2] ?? "")) {
        const character = bubbleMatch[2] ?? "";
        const bubble = bubbleMatch[3] ?? "";
        const missing = [];
        if (!character)
            missing.push("character");
        if (!bubble)
            missing.push("bubble");
        if (missing.length > 0) {
            const card = createCard("character.bubble", `Bubble ${character || "?"}`, source, index);
            card.fields = {
                bubble,
                character,
                concurrent: bubbleMatch[1] === "&",
                incomplete: true,
                missingFields: missing
            };
            return card;
        }
    }
    const sfxMatch = line.match(SFX_LENIENT);
    if (sfxMatch) {
        const card = createCard("sfx", "SFX: ?", source, index);
        card.fields = {
            concurrent: sfxMatch[1] === "&",
            incomplete: true,
            missingFields: ["sound"],
            sound: ""
        };
        return card;
    }
    const musicMatch = line.match(MUSIC_LENIENT);
    if (musicMatch) {
        const card = createCard("music", "Music: ?", source, index);
        card.fields = {
            action: "play",
            concurrent: musicMatch[1] === "&",
            incomplete: true,
            missingFields: ["track"],
            track: ""
        };
        return card;
    }
    const bgMatch = line.match(BG_LENIENT);
    if (bgMatch && !bgMatch[2]) {
        const card = createCard("bg", "bg ?", source, index);
        card.fields = {
            bg: "",
            concurrent: bgMatch[1] === "&",
            incomplete: true,
            missingFields: ["bg"],
            transition: bgMatch[3] ?? ""
        };
        return card;
    }
    const affectionMatch = line.match(AFFECTION_LENIENT);
    if (affectionMatch) {
        const character = affectionMatch[2] ?? "";
        const delta = affectionMatch[3] ?? "";
        const missing = [];
        if (!character)
            missing.push("character");
        if (!delta)
            missing.push("delta");
        if (missing.length > 0) {
            const card = createCard("record", `Affection: ${character || "?"}`, source, index);
            card.fields = {
                character,
                concurrent: affectionMatch[1] === "&",
                delta,
                incomplete: true,
                missingFields: missing,
                recordType: "affection"
            };
            return card;
        }
    }
    return undefined;
}
function emitCard(card) {
    switch (card.kind) {
        case "bg":
            return `${prefix(card)}bg set ${stringField(card, "bg")}${optional(" ", stringField(card, "transition"))}`;
        case "character.show":
            return `${prefix(card)}${stringField(card, "character")} ${stringField(card, "look")}${optional(" ", stringField(card, "transition"))}`;
        case "character.bubble":
            return `${prefix(card)}${stringField(card, "character")} bubble ${stringField(card, "bubble")}`;
        case "dialogue":
        case "narration":
        case "monologue":
            return `${stringField(card, "character")}${optionalLook(card)}: ${stringField(card, "text")}`;
        case "cg":
            return `${prefix(card)}cg ${stringField(card, "cg")} "${escapeQuote(stringField(card, "content"))}"`;
        case "phone":
            return emitPhone(card);
        case "music":
            return stringField(card, "action") === "stop"
                ? `${prefix(card)}music stop`
                : `${prefix(card)}music ${stringField(card, "track")}`;
        case "sfx":
            return `${prefix(card)}sfx ${stringField(card, "sound")}`;
        case "trick":
            return `${prefix(card)}trick ${stringField(card, "trick")} "${escapeQuote(stringField(card, "description"))}"`;
        case "minigame":
            return `${prefix(card)}minigame ${stringField(card, "minigame")} "${escapeQuote(stringField(card, "description"))}"`;
        case "choice":
            return block(`${prefix(card)}choice {`, choiceBodyLines(card));
        case "record":
            return emitRecord(card);
        case "condition":
            return block(`${prefix(card)}if (${stringField(card, "condition")}) {`, bodyLines(card));
        case "pause":
            return `${prefix(card)}pause`;
        case "gate":
            return block(`${prefix(card)}gate {`, bodyLines(card));
        default:
            return card.source;
    }
}
function emitPhone(card) {
    const messages = stringArrayField(card, "messages");
    const messageLines = messages.length > 0 ? messages : blockBody(card.source).split("\n").filter(Boolean);
    return block(`${prefix(card)}phone {`, messageLines);
}
function emitRecord(card) {
    switch (stringField(card, "recordType")) {
        case "affection":
            return `${prefix(card)}affection ${stringField(card, "character")} ${stringField(card, "delta") || "+1"}`;
        case "signal.int":
            return `${prefix(card)}signal int ${stringField(card, "name")} ${stringField(card, "op") || "="} ${numberField(card, "value", 0)}`;
        case "signal.mark":
            return `${prefix(card)}signal mark ${stringField(card, "event")}`;
        case "butterfly":
            return `${prefix(card)}butterfly "${escapeQuote(stringField(card, "description"))}"`;
        case "achievement":
            return block(`${prefix(card)}achievement ${stringField(card, "achievement")} {`, [
                `name: "${escapeQuote(stringField(card, "name"))}"`,
                `rarity: ${stringField(card, "rarity") || "uncommon"}`,
                `description: "${escapeQuote(stringField(card, "description"))}"`
            ]);
        default:
            return card.source;
    }
}
function episodeBodyLines(lines) {
    const start = lines.findIndex((line) => line.trim().startsWith("@episode"));
    if (start < 0) {
        return lines;
    }
    const body = lines.slice(start + 1);
    while (body.length > 0 && (body[body.length - 1]?.trim() ?? "") === "") {
        body.pop();
    }
    if ((body[body.length - 1]?.trim() ?? "") === "}") {
        body.pop();
    }
    return body;
}
function readStatement(lines, start) {
    const first = lines[start] ?? "";
    const trimmed = first.trim();
    if (!opensBlock(trimmed)) {
        return { nextIndex: start + 1, source: trimmed };
    }
    let depth = 0;
    const collected = [];
    for (let i = start; i < lines.length; i++) {
        const line = lines[i] ?? "";
        collected.push(trimForSource(line));
        depth += braceDelta(line);
        if (depth <= 0) {
            return { nextIndex: i + 1, source: collected.join("\n") };
        }
    }
    return { nextIndex: lines.length, source: collected.join("\n") };
}
function opensBlock(line) {
    return line.includes("{") && !line.startsWith("//") && !line.startsWith("#");
}
function braceDelta(line) {
    const withoutStrings = line.replace(/"([^"\\]|\\.)*"/g, "\"\"");
    return count(withoutStrings, "{") - count(withoutStrings, "}");
}
function count(value, needle) {
    return value.split(needle).length - 1;
}
function firstLine(source) {
    return source.split("\n")[0]?.trim() ?? "";
}
function blockBody(source) {
    const lines = source.split("\n");
    if (lines.length <= 2) {
        return "";
    }
    return lines.slice(1, -1).map((line) => line.replace(/^ {2}/, "")).join("\n").trim();
}
function fieldLine(source, key) {
    const match = source.match(new RegExp(`^\\s*${key}:\\s*(.+)$`, "m"));
    return unquote(match?.[1]?.trim() ?? "");
}
function parseTextMessages(source) {
    return blockBody(source).split("\n").flatMap((line) => {
        const match = line.match(TEXT_MESSAGE_RE);
        return match?.[1] && match[2] ? [`@text ${match[1]} ${match[2]}: ${match[3] ?? ""}`] : [];
    });
}
function parseOptions(source) {
    return source.split("\n").flatMap((line) => {
        const match = line.match(OPTION_RE);
        return match?.[1] ? [`${match[1]}|${match[2] ?? "safe"}|${match[3] ?? ""}`] : [];
    });
}
function choiceBodyLines(card) {
    const body = bodyLines(card);
    if (body.length > 0) {
        return body;
    }
    const options = stringArrayField(card, "options");
    if (options.length === 0) {
        return [
            "@option A safe \"Continue\" {",
            "  NARRATOR: Continue.",
            "}"
        ];
    }
    return options.flatMap((option) => {
        const [id = "A", mode = "safe", text = "Option"] = option.split("|");
        return [`@option ${id} ${mode} "${escapeQuote(text)}" {`, "  NARRATOR: Continue.", "}"];
    });
}
function bodyLines(card) {
    const body = stringField(card, "body");
    if (!body) {
        return [];
    }
    return body.split("\n").filter((line) => line.length > 0);
}
function block(header, body) {
    const lines = [header];
    for (const line of body) {
        pushIndented(lines, line, "  ");
    }
    lines.push("}");
    return lines.join("\n");
}
function pushIndented(lines, text, indent) {
    for (const line of text.split("\n")) {
        lines.push(`${indent}${line}`);
    }
}
function trimForSource(line) {
    return line.trimEnd().replace(/^ {2}/, "");
}
function prefix(card) {
    return booleanField(card, "concurrent") ? "&" : "@";
}
function optional(prefixText, value) {
    return value ? `${prefixText}${value}` : "";
}
function optionalLook(card) {
    const look = stringField(card, "look");
    return look ? ` [${look}]` : "";
}
function stringField(card, key) {
    const value = card.fields[key];
    return typeof value === "string" ? value : "";
}
function numberField(card, key, fallback) {
    const value = card.fields[key];
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function booleanField(card, key) {
    return card.fields[key] === true;
}
function stringArrayField(card, key) {
    const value = card.fields[key];
    return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
}
function escapeQuote(value) {
    return value.replaceAll("\"", "\\\"");
}
function unquote(value) {
    const trimmed = value.trim();
    if (trimmed.startsWith("\"") && trimmed.endsWith("\"")) {
        return trimmed.slice(1, -1).replaceAll("\\\"", "\"");
    }
    return trimmed;
}
function isKnownDirective(value) {
    return KNOWN_DIRECTIVES.has(value);
}
function normalizeEntries(value, baseUrl = "") {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return Object.entries(value).flatMap(([name, entry]) => {
            if (typeof entry === "string") {
                return [{ name, path: resolveAssetPath(baseUrl, entry) }];
            }
            if (!entry || typeof entry !== "object") {
                return [];
            }
            const record = entry;
            const label = typeof record.name === "string" ? record.name : typeof record.id === "string" ? record.id : name;
            const assetPath = typeof record.path === "string" ? record.path : typeof record.file === "string" ? record.file : "";
            return [{
                    duration: typeof record.duration === "number" ? record.duration : undefined,
                    name: label,
                    path: resolveAssetPath(baseUrl, assetPath),
                    thumbnail: typeof record.thumbnail === "string" ? resolveAssetPath(baseUrl, record.thumbnail) : undefined
                }];
        });
    }
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((entry) => {
        if (typeof entry === "string") {
            return [{ name: entry, path: resolveAssetPath(baseUrl, entry) }];
        }
        if (!entry || typeof entry !== "object") {
            return [];
        }
        const record = entry;
        const name = typeof record.name === "string" ? record.name : typeof record.id === "string" ? record.id : "";
        const assetPath = typeof record.path === "string" ? record.path : typeof record.file === "string" ? record.file : "";
        if (!name) {
            return [];
        }
        return [{
                duration: typeof record.duration === "number" ? record.duration : undefined,
                name,
                path: resolveAssetPath(baseUrl, assetPath),
                thumbnail: typeof record.thumbnail === "string" ? resolveAssetPath(baseUrl, record.thumbnail) : undefined
            }];
    });
}
/** Normalize {id|name, path} entries (array or dict form) into id-keyed
 *  entries, dropping path-less rows — a synced asset without a URL is
 *  meaningless. Powers the portraits / sprites extension arrays. */
function normalizeIdEntries(value, baseUrl = "") {
    return normalizeEntries(value, baseUrl).flatMap((entry) => entry.path ? [{ id: entry.name, path: entry.path }] : []);
}
/** Normalize outfit-anchor entries. Array-of-records only — an anchor needs
 *  an explicit (characterId, outfitId) pair; splitting a joined key would
 *  be ambiguous because character ids themselves contain underscores. */
function normalizeAnchors(value, baseUrl = "") {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((entry) => {
        if (!entry || typeof entry !== "object") {
            return [];
        }
        const record = entry;
        const characterId = typeof record.characterId === "string" ? record.characterId : typeof record.char === "string" ? record.char : "";
        const outfitId = typeof record.outfitId === "string" ? record.outfitId : typeof record.outfit === "string" ? record.outfit : "";
        const assetPath = typeof record.path === "string" ? record.path : typeof record.file === "string" ? record.file : "";
        if (!characterId || !outfitId || !assetPath) {
            return [];
        }
        return [{ characterId, outfitId, path: resolveAssetPath(baseUrl, assetPath) }];
    });
}
function normalizeCharacters(value, baseUrl = "") {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return Object.entries(value).flatMap(([id, looks]) => {
            if (!looks || typeof looks !== "object") {
                return [];
            }
            return [{
                    id,
                    looks: normalizeEntries(looks, baseUrl),
                    name: id
                }];
        });
    }
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((entry) => {
        if (!entry || typeof entry !== "object") {
            return [];
        }
        const record = entry;
        const id = typeof record.id === "string" ? record.id : typeof record.name === "string" ? record.name : "";
        if (!id) {
            return [];
        }
        return [{
                id,
                looks: normalizeEntries(record.looks, baseUrl),
                name: typeof record.name === "string" ? record.name : id
            }];
    });
}
function resolveAssetPath(baseUrl, assetPath) {
    if (!baseUrl || !assetPath || /^[a-z][a-z0-9+.-]*:/i.test(assetPath) || assetPath.startsWith("/")) {
        return assetPath;
    }
    return `${baseUrl.replace(/\/+$/, "")}/${assetPath.replace(/^\/+/, "")}`;
}
